import { isPresent } from "@apl-digital/utils";

import { hasData } from "@/store/apiRequestStatusMachine";
import type { AppStartListening } from "@/store/listenerMiddleware";
import { selectConfiguration } from "@/store/salesPoint/slice";
import {
  removeLinesFromCart,
  selectCurrentTransactionId,
  selectShoppingCartProducts,
} from "@/store/shoppingCart";
import { selectUserData } from "@/store/user";
import { isStatus } from "@/store/utils";

import { selectCurrentDiningOption } from "../shoppingCart/selectors";

import { fetchProductLayout } from "./actions";
import { selectProductLayout } from "./slice";

export const addProductsListeners = (startListening: AppStartListening) => {
  startListening({
    predicate: (_action, currentState, previousState) => {
      const configChanged =
        currentState.salesPoint.configuration !==
        previousState.salesPoint.configuration;
      const userChanged =
        currentState.user.userData !== previousState.user.userData;
      const authChanged =
        currentState.user.isAuthReady !== previousState.user.isAuthReady;
      const languageChanged =
        currentState.shoppingCart.shoppingCartLanguage !==
        previousState.shoppingCart.shoppingCartLanguage;

      // diningOption is derived from shoppingCart + salesPoint, check both
      const shoppingCartChanged =
        currentState.shoppingCart.shoppingCart !==
        previousState.shoppingCart.shoppingCart;

      return (
        configChanged ||
        userChanged ||
        authChanged ||
        languageChanged ||
        shoppingCartChanged
      );
    },
    effect: (_action, { getState, dispatch }) => {
      const state = getState();

      if (!state.user.isAuthReady) {
        return;
      }

      const configuration = selectConfiguration(state);

      if (!hasData(configuration)) {
        return;
      }

      const layoutID = configuration.state.ProductSalesLayoutId;
      const diningOption = selectCurrentDiningOption(state);

      if (!isPresent(diningOption)) {
        return;
      }

      const userData = selectUserData(state);
      const productLayout = selectProductLayout(state);

      const currentUserId = hasData(productLayout)
        ? productLayout.state.userId
        : undefined;

      const isUserEmpty = !hasData(productLayout) && !userData;
      const isNewUserIsEmpty =
        hasData(productLayout) &&
        userData?.ID === undefined &&
        currentUserId !== 0;
      const isNewUserIsDifferent =
        userData?.ID !== undefined && currentUserId !== userData?.ID;

      const hasValidProps = Boolean(layoutID);

      if (
        (isUserEmpty || isNewUserIsEmpty || isNewUserIsDifferent) &&
        hasValidProps
      ) {
        void dispatch(
          fetchProductLayout({
            productLayoutID: layoutID,
            diningOptionId: diningOption.ID,
          }),
        );
      }
    },
  });

  startListening({
    predicate: (_action, currentState, previousState) =>
      currentState.products.productLayout !==
        previousState.products.productLayout ||
      currentState.shoppingCart.shoppingCart !==
        previousState.shoppingCart.shoppingCart,
    effect: (_action, { getState, dispatch }) => {
      const state = getState();
      const productLayout = selectProductLayout(state);

      if (!hasData(productLayout)) {
        return;
      }

      const shoppingCart = state.shoppingCart.shoppingCart;

      // Avoid re-dispatching removals while cart is transitioning (stalePending).
      if (!isStatus(shoppingCart, "succeeded")) {
        return;
      }

      const products = productLayout.state.products;
      const transactionId = selectCurrentTransactionId(state);

      if (!isPresent(transactionId) || !products) {
        return;
      }

      const shoppingCartProducts = selectShoppingCartProducts(state);
      const productIds = new Set(products.map((p) => p.id));

      for (const cartProduct of shoppingCartProducts) {
        if (cartProduct.isSurchargeItem || cartProduct.isPackagingItem) {
          continue;
        }

        if (!productIds.has(cartProduct.productId)) {
          const cartLineIds = cartProduct.mergedCartLineIds ?? [];

          if (cartLineIds.length === 0) {
            continue;
          }

          void dispatch(
            removeLinesFromCart({
              cartLineIds,
              transactionId,
            }),
          );
        }
      }
    },
  });
};
